import { useMemo, useState, type FormEvent } from 'react';

import { IconPrinter, IconRefreshCw, IconSearch } from '../../components/layout/icons';
import { EmptyState, ErrorState, LoadingState } from '../../components/ui/Feedback';
import { ExcelExportButton } from '../../components/ui/ExcelExportButton';
import { ApiError } from '../../lib/api-client';
import { exportRowsToExcel } from '../../lib/excel-export';
import { currentMonthDateFilters, emptyDateFilters, todayIso, type DateFilters } from './report-filters';
import { ReportDonutChartCard, type ReportChartDatum } from './ReportAnalytics';
import { reportChartColor } from './report-chart-colors';
import { useMyCollectionReport } from './reports.queries';
import type { AmountBucket, ReportSection } from './types';

const sectionKeys: ReportSection[] = ['opd', 'emg', 'ipd', 'daycare', 'dialysis', 'ot', 'investigation'];

const sections: { key: string; label: string; sourceKeys: ReportSection[] }[] = [
  { key: 'opd', label: 'OPD Patient', sourceKeys: ['opd'] },
  { key: 'emg', label: 'EMG Patient', sourceKeys: ['emg'] },
  { key: 'ipd-daycare', label: 'IPD & DAYCARE Patient', sourceKeys: ['ipd', 'daycare'] },
  { key: 'dialysis', label: 'DIALYSIS Patient', sourceKeys: ['dialysis'] },
  { key: 'ot', label: 'OT Patient', sourceKeys: ['ot'] },
  { key: 'investigation', label: 'Investigation Patient', sourceKeys: ['investigation'] },
];

const formatAmount = (value: number) => value.toLocaleString('en-IN', { maximumFractionDigits: 2 });

const PdfIcon = () => (
  <svg viewBox="0 0 24 24" aria-hidden="true">
    <path d="M6 2h8l4 4v16H6z" fill="none" stroke="currentColor" strokeWidth="2" strokeLinejoin="round" />
    <path d="M14 2v5h5M8 16h8M8 12h5" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
  </svg>
);

export const MyCollectionPage = () => {
  const [draft, setDraft] = useState<DateFilters>(currentMonthDateFilters);
  const [filters, setFilters] = useState<DateFilters>(draft);
  const query = useMyCollectionReport(filters);
  const myRow = query.data?.userRows[0] ?? null;
  const totals = query.data?.summary ?? { cash: 0, bank: 0, refund: 0, tds: 0, total: 0 };
  const reset = () => {
    const next = emptyDateFilters();
    setDraft(next);
    setFilters(next);
  };
  const periodLabel = filters.fromDate || filters.toDate
    ? `${filters.fromDate || 'earliest'} to ${filters.toDate || 'latest'}`
    : 'all dates';
  const errorMessage = query.error instanceof ApiError ? query.error.message : 'My collection report could not be loaded.';
  const sectionAmounts = useMemo(() => {
    const amounts = Object.fromEntries(sectionKeys.map((key) => [key, {
      cash: 0,
      bank: 0,
      refund: 0,
      total: 0,
    }])) as Record<ReportSection, AmountBucket>;
    for (const row of query.data?.dateRows ?? []) {
      for (const key of sectionKeys) {
        amounts[key].cash += row.sections[key].cash;
        amounts[key].bank += row.sections[key].bank;
        amounts[key].refund += row.sections[key].refund;
        amounts[key].total += row.sections[key].total;
      }
    }
    return amounts;
  }, [query.data?.dateRows]);
  const displaySectionAmounts = useMemo(() => Object.fromEntries(sections.map((section) => [
    section.key,
    section.sourceKeys.reduce<AmountBucket>((amounts, sourceKey) => ({
      cash: amounts.cash + sectionAmounts[sourceKey].cash,
      bank: amounts.bank + sectionAmounts[sourceKey].bank,
      refund: amounts.refund + sectionAmounts[sourceKey].refund,
      total: amounts.total + sectionAmounts[sourceKey].total,
    }), { cash: 0, bank: 0, refund: 0, total: 0 }),
  ])), [sectionAmounts]);
  const sectionChartData = useMemo<ReportChartDatum[]>(() => sections.map((section, index) => ({
    name: section.label.replace(' Patient', ''),
    value: displaySectionAmounts[section.key]?.total ?? 0,
    color: reportChartColor(index),
  })), [displaySectionAmounts]);

  const exportToExcel = async () => {
    const exportRows = sections.map((section) => ({
      section: section.label,
      ...displaySectionAmounts[section.key],
    }));
    await exportRowsToExcel({
      fileName: `My-Collection-${new Date().toISOString().slice(0, 10)}`,
      title: 'My Collection',
      subtitle: periodLabel,
      columns: [
        { header: 'Section', width: 24, map: (entry) => entry.section },
        { header: 'Cash', width: 14, align: 'right', numFmt: '#,##0.00', map: (entry) => entry.cash },
        { header: 'Bank', width: 14, align: 'right', numFmt: '#,##0.00', map: (entry) => entry.bank },
        { header: 'Refund', width: 14, align: 'right', numFmt: '#,##0.00', map: (entry) => entry.refund },
        { header: 'Total', width: 14, align: 'right', numFmt: '#,##0.00', map: (entry) => entry.total },
      ],
      rows: [
        ...exportRows,
        { section: 'Grand Total', cash: totals.cash, bank: totals.bank, refund: totals.refund, total: totals.total },
      ],
    });
  };

  const submitSearch = (event: FormEvent) => {
    event.preventDefault();
    setFilters(draft);
  };

  return (
    <div className="page-stack collection-reference-page">
      <section className="content-card collection-report-card collection-reference-report my-collection-report">
        <header className="general-settings-titlebar"><h2>My Collection</h2></header>

        <form className="collection-reference-toolbar report-print-hidden" onSubmit={submitSearch}>
          <div className="collection-reference-dates">
            <label>
              <span className="sr-only">From date</span>
              <input
                aria-label="From date"
                type="date"
                value={draft.fromDate}
                max={draft.toDate || todayIso()}
                onChange={(event) => setDraft({ ...draft, fromDate: event.target.value })}
              />
            </label>
            <label>
              <span className="sr-only">To date</span>
              <input
                aria-label="To date"
                type="date"
                value={draft.toDate}
                min={draft.fromDate || undefined}
                max={todayIso()}
                onChange={(event) => setDraft({ ...draft, toDate: event.target.value })}
              />
            </label>
          </div>
          <div className="collection-reference-actions">
            <button className="collection-reference-button search" type="submit"><IconSearch />Search</button>
            <button className="collection-reference-button reset" type="button" onClick={reset}><IconRefreshCw />Reset</button>
            <button className="collection-reference-button print" type="button" onClick={() => window.print()}><IconPrinter />Print</button>
            <ExcelExportButton className="collection-reference-excel" onExport={exportToExcel} disabled={!query.isSuccess} label="Excel" />
            <button className="collection-reference-button pdf" type="button" title="Open the print dialog to save as PDF" onClick={() => window.print()}><PdfIcon />PDF</button>
          </div>
        </form>

        {query.isLoading ? <LoadingState /> : null}
        {query.isError ? <ErrorState title="Unable to load my collection" message={errorMessage} action={{ label: 'Try again', onClick: () => void query.refetch() }} /> : null}
        {query.isSuccess && !myRow ? <EmptyState title="No collection found" message="Receipts collected by your account will appear here." /> : null}
        {query.isSuccess && myRow ? (
          <div className="my-collection-content-grid">
            <div className="my-collection-chart-column">
              <ReportDonutChartCard
                eyebrow="Section analysis"
                title="My collection by section"
                data={sectionChartData}
                height={330}
                valueKind="amount"
                variant="pie"
              />
            </div>
            <div className="my-collection-wrap">
              <table className="my-collection-table">
                <thead>
                  <tr>
                    <th className="text-left">Section</th>
                    <th>Cash</th>
                    <th>Bank</th>
                    <th>Refund</th>
                    <th>Total</th>
                  </tr>
                </thead>
                <tbody>
                  {sections.map((section) => (
                    <tr key={section.key}>
                      <td className="text-left">{section.label}</td>
                      <td>{formatAmount(displaySectionAmounts[section.key]?.cash ?? 0)}</td>
                      <td>{formatAmount(displaySectionAmounts[section.key]?.bank ?? 0)}</td>
                      <td className="my-collection-refund">{formatAmount(displaySectionAmounts[section.key]?.refund ?? 0)}</td>
                      <td>{formatAmount(displaySectionAmounts[section.key]?.total ?? 0)}</td>
                    </tr>
                  ))}
                  <tr className="my-collection-total-row">
                    <td className="text-left">Grand Total</td>
                    <td>{formatAmount(totals.cash)}</td>
                    <td>{formatAmount(totals.bank)}</td>
                    <td className="my-collection-refund">{formatAmount(totals.refund)}</td>
                    <td>{formatAmount(totals.total)}</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        ) : null}
      </section>
    </div>
  );
};
